import { motion } from 'motion/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { memo, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { logSearch } from '@/features/GlobalSearch/api/logging';
import { NoResultsFound } from '@/features/GlobalSearch/components/search/NoResults';
import { SearchResults } from '@/features/GlobalSearch/components/search/SearchResults';
import { LutsTrendingSection } from '@/features/GlobalSearch/components/trending/LutsTrendingSection';
import { MotionTrendingSection } from '@/features/GlobalSearch/components/trending/MotionTrendingSection';
import { MusicTrendingSection } from '@/features/GlobalSearch/components/trending/MusicTrendingSection';
import { SfxTrendingSection } from '@/features/GlobalSearch/components/trending/SfxTrendingSection';
import { StockVideoTrendingSection } from '@/features/GlobalSearch/components/trending/StockVideoTrendingSection';
import { useTrendingData } from '@/features/GlobalSearch/hooks/useTrendingData';
import { useTracking } from '@/providers/TrackingProvider';
import { ASSET_TYPE } from '@/types/asset';
import type { ComponentProps } from 'react';
import type { FormValues } from './globalSearchForm';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { SearchResponseHit, TypesenseMultiSearchResponse } from '@music-vine/setlist';
import type { MotionGraphicsDocument } from './types/search';

// Motion Graphics Grid component with optimizations
const MotionGraphicsGrid = memo(({
  data,
  closePopup,
  onTileClick
}: {
  data: SearchResponseHit<GenericSearchResult>[] | undefined;
  closePopup: () => void;
  onTileClick?: (slug: string) => void;
}) => {
  if (!data?.length) {
    return null;
  }
  const MAX_ASSETS_TO_DISPLAY = 4;
  const limitedData = data.slice(0, MAX_ASSETS_TO_DISPLAY);
  return <div>
        {/* Content with animation - optimized with reduced properties */}
        <motion.div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {limitedData.map(item => {
        if (!(item.document.name && item.document.slug)) {
          return null;
        }
        const document = item.document as MotionGraphicsDocument;
        const image = document.image ?? '';
        // Ensure the slug has the correct path prefix
        const assetUrl = document.is_lut ? `/luts/asset/${document.slug}` : `/motion-graphics/asset/${document.slug}`;
        return <motion.div animate={{
          opacity: 1
        }} initial={{
          opacity: 0
        }} key={`mg-tile-${document.id}`} transition={{
          duration: 0.2,
          delay: 0,
          ease: 'easeOut'
        }}>
                <Link className="block cursor-pointer rounded-lg bg-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-pink-500 dark:bg-gray-900" href={assetUrl} onClick={() => {
            closePopup();
            onTileClick?.(assetUrl);
          }}>
                  <div className="overflow-hidden">
                    <div className="aspect-video w-full rounded-lg bg-center bg-cover" style={{
                backgroundImage: image ? `url(${image})` : 'none'
              }} />
                  </div>

                  <div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <h3 className="truncate font-sans font-semibold text-gray-950 text-sm dark:text-white">
                        {document.name}
                      </h3>
                      {document.contributor?.name && document.contributor.name.trim() !== '' ? <p className="mt-1 inline-flex rounded-full border border-gray-200 border-solid px-2 py-0.5 text-gray-950 text-xs dark:border-gray-800 dark:text-white">
                          {document.contributor.name}
                        </p> : null}
                    </div>
                  </div>
                </Link>
              </motion.div>;
      })}
        </motion.div>
      </div>;
});
MotionGraphicsGrid.displayName = 'MotionGraphicsGrid';
interface GlobalSearchPopupProps {
  activeAssetType: AssetType;
  closePopup: () => void;
  results?: TypesenseMultiSearchResponse<GenericSearchResult>['results'];
  searchTerm?: string;
}
export const GlobalSearchPopup = memo(({
  searchTerm,
  results,
  closePopup,
  activeAssetType
}: GlobalSearchPopupProps) => {
  const router = useRouter();
  const {
    initializeTracking
  } = useTracking();
  const methods = useFormContext<FormValues>();
  const hasNoResults = useMemo(() => {
    if (!(searchTerm && results)) {
      return false;
    }
    return results.every((result: {
      found?: number;
      hits?: unknown[];
    }) => !(result.found && result.hits) || result.hits.length === 0);
  }, [searchTerm, results]);
  const {
    trendingData,
    isLoading
  } = useTrendingData();
  const trackTrendingClick = useCallback((endSlug: string) => {
    void logSearch('', router.asPath, endSlug, 'TRENDING');
  }, [router.asPath]);
  const handleNameClick = useCallback((content: string | undefined, endSlug: string | undefined, assetType: AssetType | 'general') => {
    if (!(content && endSlug)) {
      return;
    }
    void initializeTracking({
      endSlug,
      content,
      access: 'Search',
      assetType
    });
    trackTrendingClick(endSlug);
  }, [initializeTracking, trackTrendingClick]);
  if (hasNoResults) {
    return <NoResultsFound activeAssetType={activeAssetType} onTryPopularSearch={term => {
      if (methods.setValue) {
        methods.setValue('keyword', term);
      }
    }} searchTerm={searchTerm} />;
  }
  if (searchTerm) {
    return <SearchResults closePopup={closePopup} results={results} searchTerm={searchTerm} />;
  }
  if (isLoading) {
    return null;
  }
  switch (activeAssetType) {
    case 'music':
      return <MusicTrendingSection closePopup={closePopup} onNameClick={handleNameClick} trackTrendingClick={trackTrendingClick} trendingArtists={trendingData.music.artists} trendingMusicSearches={trendingData.music.searches} trendingPlaylists={trendingData.music.playlists} />;
    case 'sfx':
      return <SfxTrendingSection closePopup={closePopup} onNameClick={handleNameClick} trackTrendingClick={trackTrendingClick} trendingSfxCollections={trendingData.sfx.collections} trendingSfxSearches={trendingData.sfx.searches} />;
    case 'motion':
      return <MotionTrendingSection closePopup={closePopup} onNameClick={handleNameClick} trackTrendingClick={trackTrendingClick} trendingMotionAssets={trendingData.motion.assets} trendingMotionSearches={trendingData.motion.searches} />;
    case ASSET_TYPE.STOCK_VIDEO:
      return <StockVideoTrendingSection closePopup={closePopup} onNameClick={handleNameClick} trackTrendingClick={trackTrendingClick} trendingStockVideoAssets={trendingData.stock_footage.assets} trendingStockVideoCategories={trendingData.stock_footage.categories as ComponentProps<typeof StockVideoTrendingSection>['trendingStockVideoCategories']} />;
    case 'luts':
      return <LutsTrendingSection closePopup={closePopup} onNameClick={handleNameClick} trackTrendingClick={trackTrendingClick} trendingLutsAssets={trendingData.luts.assets} />;
    default:
      return null;
  }
});
GlobalSearchPopup.displayName = 'GlobalSearchPopup';